import store from "@/store";

const RECONNECT_BASE_MS = 3_000;
const RECONNECT_MAX_MS = 30_000;

class UserSyncSSEService {
	constructor({ topics = [], authorization = null } = {}) {
		this.topics = topics;
		this.authorization = authorization;
		this.listeners = [];
		this.eventSource = null;
		this.reconnectTimer = null;
		this.reconnectAttempts = 0;
		this.intentionalDisconnect = false;
	}

	connect() {
		this.intentionalDisconnect = false;
		this.openEventSource();
	}

	disconnect() {
		this.intentionalDisconnect = true;
		this.clearReconnectTimer();
		if (this.eventSource) {
			this.eventSource.close();
			this.eventSource = null;
		}
	}

	addMessageListener(listener) {
		this.listeners.push(listener);
	}

	removeMessageListener(listener) {
		this.listeners = this.listeners.filter((l) => l !== listener);
	}

	openEventSource() {
		if (this.eventSource) {
			this.eventSource.close();
			this.eventSource = null;
		}

		const { sseUrl } = store.state.config;
		if (!sseUrl || !this.topics.length) {
			this.emit("error", "Missing SSE configuration");
			return;
		}

		this.emit("connecting");

		const url = new URL(sseUrl);
		this.topics.forEach((topic) => url.searchParams.append("topic", topic));
		// Private Mercure updates require a subscriber JWT. WePass is on wepass.ai, so the
		// mercureAuthorization cookie (Domain=.wewelcom.io) is never stored — pass the JWT
		// from /mercure/subscribe as the hub's `authorization` query param.
		if (this.authorization) {
			url.searchParams.set("authorization", this.authorization);
		}

		// Do not send cookies. WePass is on wepass.ai; EventSource hits sse*.wewelcom.io.
		// withCredentials would attach a leftover Copilot mercureAuthorization cookie
		// (Domain=.wewelcom.io). Mercure prefers that cookie over the query-param JWT, so
		// a copilot-only token would silently drop private user-topic events.
		this.eventSource = new EventSource(url.toString());

		this.eventSource.onopen = () => {
			this.reconnectAttempts = 0;
			this.emit("connected");
		};

		this.eventSource.onmessage = (event) => {
			try {
				this.emit("message", JSON.parse(event.data));
			} catch (e) {
				console.warn("[UserSyncSSEService] Failed to parse event", e);
			}
		};

		this.eventSource.onerror = () => {
			this.eventSource?.close();
			this.eventSource = null;
			this.emit("disconnected");
			if (!this.intentionalDisconnect) {
				this.scheduleReconnect();
			}
		};
	}

	scheduleReconnect() {
		this.clearReconnectTimer();
		const delay = Math.min(RECONNECT_BASE_MS * Math.pow(1.5, this.reconnectAttempts), RECONNECT_MAX_MS);
		this.reconnectAttempts++;
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			this.openEventSource();
		}, delay);
	}

	clearReconnectTimer() {
		if (this.reconnectTimer) {
			clearTimeout(this.reconnectTimer);
			this.reconnectTimer = null;
		}
	}

	emit(type, data) {
		this.listeners.forEach((listener) => {
			try {
				listener(type, data);
			} catch (e) {
				console.error("[UserSyncSSEService] Listener error", e);
			}
		});
	}
}

export default UserSyncSSEService;
